import {
  arrIncludesArrayItem,
  Filter,
  numberInRange,
  resolveTableUpdater,
  TableContext,
  ToggleButton,
  usePersistentTableState,
} from '@eye-able/ui';
import type { TableContextType, TableRef, TableState } from '@eye-able/ui';
import {
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type {
  AccessorKeyColumnDef,
  ColumnDef,
  RowData,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type Ref,
  type ComponentType,
  type ReactNode,
} from 'react';
import TableItemCard from '../Card/TableItemCard';
import { LayoutGrid } from 'lucide-react';
import { T } from '@/i18n/T';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

export type CardTableView = 'list' | 'grid';

export type CardTableRowRendererProps<TData extends RowData> = {
  row: TData;
  view: CardTableView;
};

export type CardTableRowTransformContext = {
  sorting: SortingState;
  view: CardTableView;
};

export type CardTableProps<TData extends RowData> = {
  actions?: ReactNode;
  data?: TData[];
  columns?: ColumnDef<TData, unknown>[];
  id?: string;
  searchColumnId?: string;
  defaultSortBy?: string;
  defaultSortDirection?: 'asc' | 'desc';
  defaultView?: CardTableView;
  onRowClick?: (row: TData) => void;
  getRowId?: (row: TData, index: number) => string | number;
  rowTitle: ComponentType<CardTableRowRendererProps<TData>>;
  rowMain: ComponentType<CardTableRowRendererProps<TData>>;
  rowTitleAction?: ComponentType<CardTableRowRendererProps<TData>>;
  transformRows?: (
    rows: TData[],
    context: CardTableRowTransformContext,
  ) => TData[];
};

interface CardTableComponent {
  <TData extends RowData = RowData>(
    props: CardTableProps<TData> & { ref?: Ref<TableRef<TData>> },
  ): JSX.Element;
}

function getColumnId<TData extends RowData>(
  column: ColumnDef<TData, unknown> | undefined,
): string | undefined {
  if (!column) return undefined;
  const accessorColumn = column as AccessorKeyColumnDef<TData, unknown>;
  return (
    column.id ??
    (typeof accessorColumn.accessorKey === 'string'
      ? accessorColumn.accessorKey
      : undefined)
  );
}

function toCsvValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value).replaceAll('"', '""');
  return /[",\n]/.test(str) ? `"${str}"` : str;
}

function downloadCsv(filename: string, csvContent: string) {
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function CardTableInner<TData extends RowData>(
  {
    actions,
    data = [],
    columns = [],
    id = 'card-table',
    searchColumnId,
    defaultSortBy,
    defaultSortDirection = 'asc',
    defaultView = 'list',
    onRowClick,
    getRowId = (row, index) => {
      const candidate = (row as { id?: string | number }).id;
      return candidate ?? index;
    },
    rowTitle: RowTitle,
    rowMain: RowMain,
    rowTitleAction: RowTitleAction,
    transformRows,
  }: CardTableProps<TData>,
  ref: Ref<TableRef<TData>>,
) {
  const { t } = useInlineTranslation();
  const resolvedSearchColumnId = useMemo(() => {
    return searchColumnId ?? getColumnId(columns[0]);
  }, [columns, searchColumnId]);

  const initialSortColumnId = useMemo(() => {
    const firstSortableColumn = columns.find(
      (column) => column.enableSorting !== false && getColumnId(column),
    );
    return defaultSortBy ?? getColumnId(firstSortableColumn);
  }, [columns, defaultSortBy]);

  const tableId = useMemo(() => {
    const columnsKey = columns
      .map(
        (column) =>
          (column as AccessorKeyColumnDef<TData, unknown>)?.accessorKey,
      )
      .sort()
      .join('_');

    return `${window.location.pathname}_${id}_${columnsKey}`;
  }, [columns, id]);

  const viewStorageKey = `${tableId}_view`;

  const defaultTableState = useMemo<TableState>(() => {
    return {
      sorting: initialSortColumnId
        ? [
            {
              id: initialSortColumnId,
              desc: defaultSortDirection === 'desc',
            },
          ]
        : [],
      columnFilters: [],
      columnVisibility: {},
    };
  }, [defaultSortDirection, initialSortColumnId]);

  const [view, setView] = useState<CardTableView>(() => {
    const storedView = localStorage.getItem(viewStorageKey);

    return storedView === 'grid' || storedView === 'list'
      ? storedView
      : defaultView;
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [tableState, setTableState] = usePersistentTableState(
    tableId,
    defaultTableState,
  );

  useEffect(() => {
    localStorage.setItem(viewStorageKey, view);
  }, [viewStorageKey, view]);

  const handleSortingChange = (updater: Updater<SortingState>) => {
    setTableState((prev) => {
      const next = resolveTableUpdater(updater, prev.sorting ?? []);
      if (!next.length) {
        return {
          ...prev,
          sorting: next,
        };
      }

      const nextPrimary = next[0];
      const prevPrimary = prev.sorting?.[0];
      const isNewColumn = prevPrimary?.id !== nextPrimary.id;

      let resolvedSorting = next;

      if (isNewColumn && !nextPrimary.desc) {
        const column = columns.find(
          (col) => getColumnId(col) === nextPrimary.id,
        );
        if (column?.sortDescFirst) {
          resolvedSorting = [{ ...nextPrimary, desc: true }, ...next.slice(1)];
        }
      }

      return {
        ...prev,
        sorting: resolvedSorting,
      };
    });
  };
  const table = useReactTable<TData>({
    data,
    columns,
    state: {
      globalFilter,
      columnFilters: tableState.columnFilters,
      columnVisibility: tableState.columnVisibility,
      sorting: tableState.sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: (updater) =>
      setTableState((prev) => ({
        ...prev,
        columnFilters: resolveTableUpdater(updater, prev.columnFilters ?? []),
      })),
    onColumnVisibilityChange: (updater) =>
      setTableState((prev) => ({
        ...prev,
        columnVisibility: resolveTableUpdater(
          updater,
          prev.columnVisibility ?? {},
        ),
      })),
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const normalizedFilter = String(filterValue ?? '')
        .trim()
        .toLowerCase();

      if (!normalizedFilter || !resolvedSearchColumnId) return true;

      const columnValue = String(row.getValue(resolvedSearchColumnId) ?? '')
        .trim()
        .toLowerCase();
      return columnValue.includes(normalizedFilter);
    },
    filterFns: {
      arrIncludesArrayItem: (row, columnId, filterValues, addMeta) => {
        if (arrIncludesArrayItem(row, columnId, filterValues, addMeta)) {
          return true;
        }

        if (!Array.isArray(filterValues) || filterValues.length === 0) {
          return false;
        }

        const cellValue = row.getValue(columnId);
        if (Array.isArray(cellValue)) {
          return cellValue.some((value) => filterValues.includes(value));
        }

        if (typeof cellValue === 'string') {
          const normalizedCellValues = cellValue
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean);
          return normalizedCellValues.some((value) =>
            filterValues.includes(value),
          );
        }

        return false;
      },
      numberInRange,
    },
  });

  const filteredAndSortedRows = table.getSortedRowModel().rows;

  const renderedRows = useMemo(() => {
    const rows = filteredAndSortedRows.map((tableRow) => tableRow.original);

    if (!transformRows) {
      return rows;
    }

    return transformRows(rows, {
      sorting: tableState.sorting,
      view,
    });
  }, [filteredAndSortedRows, tableState.sorting, transformRows, view]);

  const contextValue = useMemo<Partial<TableContextType<TData>>>(() => {
    return {
      globalFilter,
      setGlobalFilter,
      table,
      tableState,
    };
  }, [globalFilter, table, tableState]);

  useImperativeHandle(
    ref,
    () => ({
      table,
      tableId,
      exportTableData: () => {
        const exportableColumns = columns.filter((column) =>
          getColumnId(column),
        );
        const headers = exportableColumns.map((column) => {
          const header = column.header;
          return typeof header === 'string'
            ? header
            : String(getColumnId(column) ?? '');
        });
        const rows = table.getPrePaginationRowModel().rows.map((row) => {
          return exportableColumns.map((column) => {
            const columnId = getColumnId(column);
            return toCsvValue(columnId ? row.getValue(columnId) : '');
          });
        });

        const csvContent = [
          headers.map(toCsvValue).join(','),
          ...rows.map((row) => row.join(',')),
        ].join('\n');

        downloadCsv('table-export', csvContent);
      },
    }),
    [columns, table, tableId],
  );

  return (
    <TableContext.Provider
      value={contextValue as unknown as TableContextType<unknown>}
    >
      <div className='eye:flex eye:w-full eye:flex-col'>
        <Filter
          disableFullscreen
          disableHideShowColumns
          actions={
            <div className='eye:flex eye:items-center eye:gap-4xs eye:mr-2xs'>
              {actions}
              <ToggleButton
                pressed={view === 'grid'}
                aria-label={view === 'grid' ? t('listView') : t('gridView')}
                className='eye:bg-white'
                onPressedChange={(pressed) => {
                  setView(pressed ? 'grid' : 'list');
                }}
              >
                <LayoutGrid className='eye:stroke-[1.5]' aria-hidden />
              </ToggleButton>
            </div>
          }
        />

        {renderedRows.length === 0 ? (
          <div className='eye:body-m eye:py-16 eye:text-center eye:text-grey-100'>
            <T k='noResultsFound' />
          </div>
        ) : (
          <div className='eye:w-full eye:overflow-auto eye:max-h-[70dvh] eye:p-4xs'>
            <div
              className={
                view === 'grid'
                  ? 'eye:grid eye:grid-cols-1 eye:gap-2xs eye:md:grid-cols-2 eye:xl:grid-cols-3'
                  : 'eye:flex eye:flex-col eye:gap-2xs'
              }
            >
              {renderedRows.map((row, index) => {
                const rowId = getRowId(row, index);
                const rowRendererProps = { row, view };

                return (
                  <TableItemCard
                    key={rowId}
                    title={<RowTitle {...rowRendererProps} />}
                    titleAction={
                      RowTitleAction ? (
                        <RowTitleAction {...rowRendererProps} />
                      ) : null
                    }
                    main={<RowMain {...rowRendererProps} />}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </TableContext.Provider>
  );
}

const CardTable = forwardRef(CardTableInner) as CardTableComponent;

export default CardTable;
